import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, FileCheck2, Send, Download, X, RefreshCw, Link2 } from "lucide-react";

export const DOC_TYPES = ["Design", "Leadership Approval", "Decision", "Go/No-Go", "Customer Communication", "UAT Sign-off", "Project Closure", "Other"];
const ST_COLORS = { pending: "#F59E0B", approved: "#2563EB", rejected: "#EF4444" };

export default function ApprovalsView({ projectId }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [resubmitOf, setResubmitOf] = useState(null);

  const load = () => api.get(`/projects/${projectId}/approvals`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async (a) => {
    try {
      const { data } = await api.post(`/approvals/${a.id}/resend`);
      toast.success(`Reminder emailed to ${data.sent} pending approver(s)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend"); }
  };
  const remove = async (a) => {
    await api.delete(`/approvals/${a.id}`);
    toast.success("Approval request removed");
    load();
  };
  const copyLink = async (ap) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/approve/${ap.token}`);
      toast.success(`Link for ${ap.name || ap.email} copied`);
    } catch (e) { toast.error("Could not copy"); }
  };
  const downloadFile = async (a) => {
    try {
      const res = await api.get(`/approvals/${a.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const el = document.createElement("a");
      el.href = url; el.download = a.filename; el.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const counts = { pending: 0, approved: 0, rejected: 0 };
  items.forEach((a) => { counts[a.status] = (counts[a.status] || 0) + 1; });

  return (
    <div className="space-y-5" data-testid="approvals-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="font-display text-lg font-bold">Doc approvals <span className="text-sm text-muted-foreground font-normal">({items.length})</span></h3>
          <div className="flex gap-3 text-xs">
            {Object.entries(ST_COLORS).map(([k, c]) => (
              <span key={k} className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: c }} /> <b>{counts[k] || 0}</b> {k}</span>
            ))}
          </div>
        </div>
        <Button className="gap-2" onClick={() => setOpen(true)} data-testid="new-approval-button"><Plus className="h-4 w-4" /> Request approval</Button>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-10 text-center" data-testid="approvals-empty">
          <FileCheck2 className="h-8 w-8 text-primary mx-auto mb-3" />
          <p className="text-muted-foreground max-w-md mx-auto">Send designs, go/no-go docs, customer comms, UAT sign-offs and closure documents for approval. Approved docs are filed automatically in the Library under <b>Approved Docs</b>.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} className="border border-border rounded-lg p-4" style={{ borderLeft: `4px solid ${ST_COLORS[a.status] || "#94a3b8"}` }} data-testid={`approval-card-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium text-sm flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10px] font-bold text-primary">{a.display_id}</span>
                    {a.title}
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{a.doc_type}</span>
                    {a.version > 1 && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary">v{a.version}</span>}
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-white uppercase" style={{ background: ST_COLORS[a.status] }} data-testid={`approval-status-${a.id}`}>{a.status}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {a.filename} · rule: {a.rule === "any" ? "any one approver" : "all approvers"}{a.due_date ? ` · needed by ${a.due_date}` : ""} · sent {new Date(a.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => downloadFile(a)} title="Download the document" data-testid={`approval-download-${a.id}`}><Download className="h-3.5 w-3.5" /></Button>
                  {a.status === "pending" && (
                    <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => resend(a)} title="Email a reminder to pending approvers" data-testid={`approval-resend-${a.id}`}><Send className="h-3.5 w-3.5" /> Remind</Button>
                  )}
                  {a.status === "rejected" && !a.superseded_by && (
                    <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setResubmitOf(a)} data-testid={`approval-resubmit-${a.id}`}><RefreshCw className="h-3.5 w-3.5" /> Send new version</Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-8 text-destructive" onClick={() => remove(a)} title="Delete this request" data-testid={`approval-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {a.approvers.map((ap, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 text-xs border border-border rounded-full pl-2 pr-1 py-0.5" title={ap.comment ? `Comment: ${ap.comment}` : ap.email} data-testid={`approver-chip-${a.id}-${i}`}>
                    <span className="h-2 w-2 rounded-full" style={{ background: ST_COLORS[ap.status] }} />
                    {ap.name || ap.email}
                    <span className="text-muted-foreground">· {ap.status}</span>
                    {ap.token && a.status === "pending" && ap.status === "pending" && (
                      <button type="button" className="p-0.5 rounded hover:bg-secondary text-muted-foreground" onClick={() => copyLink(ap)} title="Copy this approver's link" data-testid={`approver-copylink-${a.id}-${i}`}><Link2 className="h-3 w-3" /></button>
                    )}
                  </span>
                ))}
              </div>
              {a.approvers.some((ap) => ap.comment) && (
                <div className="mt-2 space-y-1">
                  {a.approvers.filter((ap) => ap.comment).map((ap, i) => (
                    <p key={ap.email || ap.name || i} className="text-xs text-muted-foreground border-l-2 border-border pl-2 italic">{ap.name || ap.email}: "{ap.comment}"</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ApprovalDialog projectId={projectId} open={open || !!resubmitOf} previous={resubmitOf}
        onClose={() => { setOpen(false); setResubmitOf(null); }} onDone={() => { setOpen(false); setResubmitOf(null); load(); }} />
    </div>
  );
}

function ApprovalDialog({ projectId, open, previous, onClose, onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [docType, setDocType] = useState("Other");
  const [note, setNote] = useState("");
  const [due, setDue] = useState("");
  const [rule, setRule] = useState("all");
  const [approvers, setApprovers] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [people, setPeople] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      api.get("/resource-registry").then((r) => r.data).catch(() => []),
      api.get(`/projects/${projectId}/resources`).then((r) => r.data).catch(() => []),
    ]).then(([g, p]) => {
      const seen = new Set();
      const list = [];
      [...g, ...p].forEach((x) => {
        const em = (x.email || "").trim().toLowerCase();
        if (em && !seen.has(em)) { seen.add(em); list.push({ name: (x.name || "").trim(), email: em }); }
      });
      setPeople(list.sort((a, b) => a.name.localeCompare(b.name)));
    });
    if (previous) {
      setTitle(previous.title);
      setDocType(previous.doc_type);
      setNote(previous.note || "");
      setDue(previous.due_date || "");
      setRule(previous.rule);
      setApprovers(previous.approvers.map((ap) => ({ name: ap.name, email: ap.email })));
    } else {
      setTitle(""); setDocType("Other"); setNote(""); setDue(""); setRule("all"); setApprovers([]);
    }
    setFile(null); setName(""); setEmail("");
  }, [open, previous, projectId]);

  const addApprover = (n, e) => {
    const em = (e || "").trim().toLowerCase();
    if (!em || !em.includes("@")) { toast.error("Enter a valid email"); return; }
    if (approvers.some((x) => x.email === em)) return;
    setApprovers((xs) => [...xs, { name: (n || "").trim(), email: em }]);
    setName(""); setEmail("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Attach the document to approve"); return; }
    if (approvers.length === 0) { toast.error("Add at least one approver"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title || file.name);
      fd.append("doc_type", docType);
      fd.append("note", note);
      fd.append("due_date", due);
      fd.append("rule", rule);
      fd.append("approvers", JSON.stringify(approvers));
      if (previous) fd.append("previous_id", previous.id);
      const { data } = await api.post(`/projects/${projectId}/approvals`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.emails_sent > 0 ? `Sent to ${data.emails_sent} approver(s) by email` : "Request created — copy the approver links from the card (email may not be configured)");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create the request"); }
    finally { setSaving(false); }
  };

  const remaining = useMemo(() => people.filter((p) => !approvers.some((a) => a.email === p.email)), [people, approvers]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="approval-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{previous ? `New version of ${previous.display_id}` : "Request document approval"}</DialogTitle>
          <DialogDescription>Approvers get a secure email link to review and decide — no sign-in needed. Approved docs are filed in the Library under Approved Docs.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Document</Label>
            <Input ref={fileRef} type="file" className="mt-1.5" onChange={(e) => { const f = e.target.files?.[0]; setFile(f || null); if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, "")); }} data-testid="approval-file-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ap-title">Title</Label>
              <Input id="ap-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" data-testid="approval-title-input" />
            </div>
            <div>
              <Label>Doc type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1.5" data-testid="approval-doctype-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="ap-note">Note to approvers (optional)</Label>
            <Textarea id="ap-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What are they approving and why?" className="mt-1.5" data-testid="approval-note-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ap-due">Needed by (optional)</Label>
              <Input id="ap-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1.5" data-testid="approval-due-input" />
            </div>
            <div>
              <Label>Approval rule</Label>
              <Select value={rule} onValueChange={setRule}>
                <SelectTrigger className="mt-1.5" data-testid="approval-rule-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All must approve</SelectItem>
                  <SelectItem value="any">Any one approval is enough</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Approvers</Label>
            {remaining.length > 0 && (
              <Select value="" onValueChange={(v) => { const p = people.find((x) => x.email === v); if (p) addApprover(p.name, p.email); }}>
                <SelectTrigger className="mt-1.5" data-testid="approval-people-select"><SelectValue placeholder="Add from registry…" /></SelectTrigger>
                <SelectContent>
                  {remaining.map((p) => <SelectItem key={p.email} value={p.email}>{p.name || p.email} — {p.email}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-2 mt-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="h-9" data-testid="approval-name-input" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@company.com" className="h-9" data-testid="approval-email-input" />
              <Button type="button" variant="outline" className="h-9 shrink-0" onClick={() => addApprover(name, email)} data-testid="approval-add-approver">Add</Button>
            </div>
            {approvers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="approval-approver-chips">
                {approvers.map((a) => (
                  <span key={a.email} className="inline-flex items-center gap-1 text-xs bg-secondary rounded-full pl-2.5 pr-1 py-1">
                    {a.name || a.email}
                    <button type="button" className="p-0.5 rounded-full hover:bg-background" onClick={() => setApprovers((xs) => xs.filter((x) => x.email !== a.email))} data-testid={`approval-remove-${a.email}`}><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} className="gap-2" data-testid="approval-send-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send for approval
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
